import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DealerDto, NeutDto, SluitpotDto, BewerkingDto, DorpelDto, GlaslatDto } from 'src/app/shared/api-models/models';
import {map, tap} from 'rxjs/internal/operators';

@Injectable()
export class BackendService {
    private readonly dealersApiUrl = 'dealers';
    private readonly neutenApiUrl = 'neuten';
    private readonly glaslattenApiUrl = 'glaslatten';
    private readonly sluitpottenApiUrl = 'sluitpotten';
    private readonly bewerkingenApiUrl = 'bewerkingen';
    private readonly dorpelsApiUrl = 'dorpels';
    private readonly postMaakDocumentUrl = 'bestelling/PostMaakDocument';
    private readonly getOrderNoUrl = 'bestelling/OrderNumber';
    private readonly postPrefilledOrderformUrl = 'prefilled/PostPrefilledOrderform';
    private readonly addAttachmentUrl = 'bestelling/AddAttachment';
    private readonly removeAttachmentUrl = 'bestelling/RemoveAttachment';

    private _neuten: NeutDto[];
    private _glaslatten: GlaslatDto[];
    private _dealers: DealerDto[];

    public constructor(private httpClient: HttpClient) { }

    public getDealers(): Observable<DealerDto[]> {      
        if (!this._dealers) {
            return this.httpClient.get<DealerDto[]>(`${environment.baseUrl}/${this.dealersApiUrl}`)
                .pipe(tap(dealers => this._dealers = dealers));
        }
        return of(this._dealers);
    }

    public getNeuten(): Observable<NeutDto[]> {
        if (!this._neuten) {
            return this.httpClient.get<NeutDto[]>(`${environment.baseUrl}/${this.neutenApiUrl}`)
                .pipe(tap(neuten => this._neuten = neuten));
        }
        return of(this._neuten);
    }

    public getGlaslatten(): Observable<GlaslatDto[]> {
        if (!this._glaslatten) {
            return this.httpClient.get<GlaslatDto[]>(`${environment.baseUrl}/${this.glaslattenApiUrl}`)
                .pipe(tap(glaslatten => this._glaslatten = glaslatten));
        }
        return of(this._glaslatten);
    }

    public getSluitpotten(): Observable<SluitpotDto[]> {
        return this.httpClient.get<SluitpotDto[]>(`${environment.baseUrl}/${this.sluitpottenApiUrl}`);
    }

    public getBewerkingen(dorpelCode: string): Observable<BewerkingDto[]> {
        return this.httpClient.get<BewerkingDto[]>(`${environment.baseUrl}/${this.bewerkingenApiUrl}/${dorpelCode}`);
    }

    public getAllBewerkingen(): Observable<BewerkingDto[]> {
      return this.httpClient.get<BewerkingDto[]>(`${environment.baseUrl}/${this.bewerkingenApiUrl}`);
    }

    public getDorpels(productType: string): Observable<DorpelDto[]> {
        return this.httpClient.get<DorpelDto[]>(`${environment.baseUrl}/${this.dorpelsApiUrl}`)
          .pipe(map(items => items.filter(dorpel => dorpel.productsoort.toLowerCase() === productType.toLowerCase())));
    }

    public postForm(formData: FormData, orderNo: string): Observable<any> {
        return this.httpClient.post(`${environment.baseUrl}/${this.postMaakDocumentUrl}/${orderNo}`, formData);
    }

    public getOrderNo(): Observable<any> {
      return this.httpClient.get(`${environment.baseUrl}/${this.getOrderNoUrl}`);
    }

    public postPrefilledOrderform(formData: FormData): Observable<any> {
        return this.httpClient.post(`${environment.baseUrl}/${this.postPrefilledOrderformUrl}`, formData);
    }

    public uploadAttachment(file: any, orderId: string): Observable<any> {
      const fileFormData = new FormData();
      fileFormData.append('file', file);
      return this.httpClient.post(`${environment.baseUrl}/${this.addAttachmentUrl}?orderId=${orderId}`, fileFormData);
    }

    public removeAttachment(fileName: string, orderId: string): Observable<any> {
      return this.httpClient.delete(`${environment.baseUrl}/${this.removeAttachmentUrl}?orderId=${orderId}&fileName=${fileName}`);
    }
}
